import React, { useState, useEffect, useCallback } from 'react';
import { Search, Plus, Tag, Trash2, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../../config/apiBase';


export default function ResearchPage() {
    const [catalogs, setCatalogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [form, setForm] = useState({ name: '', keywords: [] });
    const [kwInput, setKwInput] = useState('');
    const [saving, setSaving] = useState(false);

    const token = localStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

    const fetchCatalogs = useCallback(async () => {
        try {
            const res = await fetch(`${API}/research-catalogs`, { headers });
            if (res.ok) setCatalogs(await res.json());
        } catch {} finally { setLoading(false); }
    }, []);

    useEffect(() => { fetchCatalogs(); }, [fetchCatalogs]);

    const handleSave = async () => {
        if (!form.name.trim()) { toast.error('Nome obrigatorio'); return; }
        setSaving(true);
        try {
            const res = await fetch(`${API}/research-catalogs`, { method: 'POST', headers, body: JSON.stringify(form) });
            if (res.ok) { toast.success('Catalogo criado'); setShowModal(false); setForm({ name: '', keywords: [] }); fetchCatalogs(); }
        } catch { toast.error('Erro'); } finally { setSaving(false); }
    };

    const toggleActive = async (id) => {
        const res = await fetch(`${API}/research-catalogs/${id}/toggle`, { method: 'PUT', headers });
        if (res.ok) { const d = await res.json(); setCatalogs(p => p.map(c => c.catalog_id === id ? { ...c, active: d.active } : c)); }
    };

    const deleteCatalog = async (id) => {
        if (!window.confirm('Excluir catalogo?')) return;
        const res = await fetch(`${API}/research-catalogs/${id}`, { method: 'DELETE', headers });
        if (res.ok) { setCatalogs(p => p.filter(c => c.catalog_id !== id)); toast.success('Excluido'); }
    };

    const addKw = () => {
        if (kwInput.trim() && !form.keywords.includes(kwInput.trim())) {
            setForm({ ...form, keywords: [...form.keywords, kwInput.trim()] });
            setKwInput('');
        }
    };

    const filtered = catalogs.filter(c => !search || c.name.toLowerCase().includes(search.toLowerCase()) || (c.keywords || []).some(k => k.includes(search.toLowerCase())));

    return (
        <div className="sv-page" data-testid="research-page">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h2 className="text-lg font-bold" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>Pesquisas Focadas</h2>
                    <p className="text-xs" style={{ color: 'var(--muted)' }}>{catalogs.length} catalogos</p>
                </div>
                <button className="sv-btn-gold-system sv-btn-system" onClick={() => { setForm({ name: '', keywords: [] }); setShowModal(true); }} data-testid="new-catalog-btn"><Plus size={14} /> Novo Catalogo</button>
            </div>

            <div className="sv-card-system">
                <div className="relative">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted-2)' }} />
                    <input type="text" placeholder="Buscar catalogo ou palavra-chave..." value={search} onChange={e => setSearch(e.target.value)}
                        className="w-full pl-9 pr-4 py-2.5 rounded-lg text-sm" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', outline: 'none' }} />
                </div>
            </div>

            {loading ? (
                <div className="sv-card-system sv-empty-state"><Loader2 className="animate-spin" /><p className="sv-empty-state-title">Carregando...</p></div>
            ) : filtered.length === 0 ? (
                <div className="sv-card-system sv-empty-state"><Search /><p className="sv-empty-state-title">{search ? 'Nenhum resultado' : 'Nenhum catalogo'}</p></div>
            ) : (
                <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))' }}>
                    {filtered.map(cat => (
                        <div key={cat.catalog_id} className="sv-card-system" data-testid={`catalog-${cat.catalog_id}`}>
                            <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)' }}>
                                        <Search size={18} style={{ color: cat.active ? 'var(--sv-gold-primary)' : 'var(--muted-2)' }} />
                                    </div>
                                    <div><p className="font-semibold text-sm" style={{ color: 'var(--text)' }}>{cat.name}</p><p className="text-xs" style={{ color: 'var(--muted-2)' }}>{(cat.keywords || []).length} palavras-chave</p></div>
                                </div>
                                <button className={`sv-badge ${cat.active ? 'sv-badge-success' : 'sv-badge-muted'} cursor-pointer`} onClick={() => toggleActive(cat.catalog_id)}>
                                    {cat.active ? 'Ativo' : 'Inativo'}
                                </button>
                            </div>
                            <div className="flex flex-wrap gap-1 mb-3">
                                {(cat.keywords || []).map(kw => (
                                    <span key={kw} className="sv-subnav-chip" style={{ fontSize: 11, padding: '3px 10px' }}><Tag size={10} /> {kw}</span>
                                ))}
                            </div>
                            <div className="flex gap-2 pt-3" style={{ borderTop: '1px solid var(--border)' }}>
                                <button className="sv-btn-system" style={{ padding: '5px 8px', color: '#ef4444' }} onClick={() => deleteCatalog(cat.catalog_id)}><Trash2 size={12} /> Excluir</button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {showModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }} onClick={e => { if (e.target === e.currentTarget) setShowModal(false); }}>
                    <div className="sv-card-system w-full max-w-lg" data-testid="catalog-modal">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-base font-bold" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>Novo Catalogo</h3>
                            <button className="sv-btn-system" style={{ padding: 4 }} onClick={() => setShowModal(false)}><X size={14} /></button>
                        </div>
                        <div className="flex flex-col gap-3">
                            <div>
                                <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--muted)' }}>Nome</label>
                                <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Ex: Eletronicos"
                                    className="w-full px-3 py-2.5 rounded-lg text-sm" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', outline: 'none' }} data-testid="catalog-name-input" />
                            </div>
                            <div>
                                <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--muted)' }}>Palavras-chave</label>
                                <div className="flex gap-2">
                                    <input type="text" value={kwInput} onChange={e => setKwInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && addKw()} placeholder="smartphone, notebook..."
                                        className="flex-1 px-3 py-2 rounded-lg text-sm" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', outline: 'none' }} />
                                    <button className="sv-btn-system" onClick={addKw}><Plus size={14} /></button>
                                </div>
                                <div className="flex flex-wrap gap-1 mt-2">
                                    {form.keywords.map(kw => (
                                        <span key={kw} className="text-xs px-2 py-1 rounded-full flex items-center gap-1" style={{ background: 'var(--surface-2)', color: 'var(--text)', border: '1px solid var(--border)' }}>
                                            {kw}<button onClick={() => setForm({ ...form, keywords: form.keywords.filter(x => x !== kw) })}><X size={10} /></button>
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 mt-5">
                            <button className="sv-btn-system" onClick={() => setShowModal(false)}>Cancelar</button>
                            <button className="sv-btn-gold-system sv-btn-system" onClick={handleSave} disabled={saving} data-testid="catalog-save-btn">
                                {saving ? <Loader2 size={14} className="animate-spin" /> : null} Criar Catalogo
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
